import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Select, Button, Row, Col, Table, message, Spin } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import PacientesSelect from '../../../components/select/PacienteSelect';
import SucursalesSelect from '../../../components/select/SucursalesSelect';
import { RootState } from '../../../redux/store/store';
import { createOptometriaNeonatos } from '../../../redux/actions/consultas/optometria_neonatos/Optometria_neonatos';
import ServiciosClinicosSelect from '../../../components/select/ServiciosSelect';

interface ServiceOption {
  value: number;
  label: string;
}

interface FormValues {
  pacienteId: number | string;
  sucursalId: number | string;
  edad: string;
  fecha_atencion: string;
  motivo_consulta: string;
  antecedente_ocular: string;
  antecedente_personal: string;
  antecedente_familiar: string;
  medicamentos: string;
  tratamiento_anterior: string;
  desarrollo: string;
  nacimiento: string;
  parto: string;
  gateo: string;
  lenguaje: string;
  complicacion_prenatal: string;
  complicacion_perinatal: string;
  complicacion_postnatal: string;
  tambor_optocinetico_AO: string;
  fija: string;
  sigue: string;
  mantiene: string;
  test_referencial_OD: string;
  test_referencial_OI: string;
  test_referencial_AO: string;
  ru_esfera_OD: string;
  ru_cilindro_OD: string;
  ru_eje_OD: string;
  ru_p_base_OD: string;
  ru_add_OD: string;
  ru_esfera_OI: string;
  ru_cilindro_OI: string;
  ru_eje_OI: string;
  ru_p_base_OI: string;
  ru_add_OI: string;
  ru_tipo_lentes: string;
  filtros: string;
  tiempo: string;
  tipo_aro: string;
  segmento_anterior: string;
  polo_posterior: string;
  hirschberg: string;
  krismsky: string;
  versiones: string;
  ct_vp: string;
  reflejo_c: string;
  ducciones_OD: string;
  ducciones_OI: string;
  posicion_compensatoria: string;
  fotomotor_OD: string;
  consensual_OD: string;
  fotomotor_OI: string;
  consensual_OI: string;
  reflejo_OD: string;
  reflejo_OI: string;
  reflejo_AO: string;
  rf_esfera_OD: string;
  rf_cilindro_OD: string;
  rf_eje_OD: string;
  rf_p_base_OD: string;
  rf_add_OD: string;
  rf_esfera_OI: string;
  rf_cilindro_OI: string;
  rf_eje_OI: string;
  rf_p_base_OI: string;
  rf_add_OI: string;
  rf_tipo_lentes: string;
  PD: string;
  uso: string;
  conducta_seguir: string;
  fecha_proxima_consulta: string;
  [key: string]: any;
}

const validationSchema = Yup.object().shape({
  pacienteId: Yup.number().required('Seleccione un paciente'),
  sucursalId: Yup.number().required('Seleccione una sucursal'),
  fecha_atencion: Yup.date().required('La fecha de atención es requerida'),
  fecha_proxima_consulta: Yup.date().required('La fecha de próxima consulta es requerida')
});

const OptometriasNeonatas = () => {
  const formikRef = React.useRef<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, success } = useSelector((state: RootState) => state.optometriaNeonatos);
  const [selectedPaciente, setSelectedPaciente] = useState<any>(null);
  const [selectedSucursal, setSelectedSucursal] = useState<any>(null);
  const [serviciosRealizados, setServiciosRealizados] = useState<ServiceOption[]>([]);
  const [serviciosProximos, setServiciosProximos] = useState<ServiceOption[]>([]);


  const columns = [
    { title: 'RX', dataIndex: 'rx', key: 'rx', align: 'center' as const },
    { title: 'ESFERA', dataIndex: 'esfera', key: 'esfera' },
    { title: 'CILINDRO', dataIndex: 'cilindro', key: 'cilindro' },
    { title: 'EJE', dataIndex: 'eje', key: 'eje' },
    { title: 'P/BASE △', dataIndex: 'p_base', key: 'p_base' },
    { title: 'ADD', dataIndex: 'add', key: 'add' },
  ];

  const dataSource = [
    {
      key: 'od',
      rx: 'Ojo Derecho',
      esfera: <Field name="ru_esfera_OD" className="w-full p-2 border rounded" />,
      cilindro: <Field name="ru_cilindro_OD" className="w-full p-2 border rounded" />,
      eje: <Field name="ru_eje_OD" className="w-full p-2 border rounded" />,
      p_base: <Field name="ru_p_base_OD" className="w-full p-2 border rounded" placeholder="△" />,
      add: <Field name="ru_add_OD" className="w-full p-2 border rounded" />,
    },
    {
      key: 'oi',
      rx: 'Ojo Izquierdo',
      esfera: <Field name="ru_esfera_OI" className="w-full p-2 border rounded" />,
      cilindro: <Field name="ru_cilindro_OI" className="w-full p-2 border rounded" />,
      eje: <Field name="ru_eje_OI" className="w-full p-2 border rounded" />,
      p_base: <Field name="ru_p_base_OI" className="w-full p-2 border rounded" placeholder="△" />,
      add: <Field name="ru_add_OI" className="w-full p-2 border rounded" />,
    },
  ];

  const dataSourceFinal = [
    {
      key: 'od',
      rx: 'Ojo Derecho',
      esfera: <Field name="rf_esfera_OD" className="w-full p-2 border rounded" />,
      cilindro: <Field name="rf_cilindro_OD" className="w-full p-2 border rounded" />,
      eje: <Field name="rf_eje_OD" className="w-full p-2 border rounded" />,
      p_base: <Field name="rf_p_base_OD" className="w-full p-2 border rounded" placeholder="△" />,
      add: <Field name="rf_add_OD" className="w-full p-2 border rounded" />,
    },
    {
      key: 'oi',
      rx: 'Ojo Izquierdo',
      esfera: <Field name="rf_esfera_OI" className="w-full p-2 border rounded" />,
      cilindro: <Field name="rf_cilindro_OI" className="w-full p-2 border rounded" />,
      eje: <Field name="rf_eje_OI" className="w-full p-2 border rounded" />,
      p_base: <Field name="rf_p_base_OI" className="w-full p-2 border rounded" placeholder="△" />,
      add: <Field name="rf_add_OI" className="w-full p-2 border rounded" />,
    },
  ];

  const handlePacienteChange = (value: string, paciente: any) => {
    setSelectedPaciente(paciente);
  };

  const handleAddServicioRealizado = (value: string, servicio: any): void => {
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && !serviciosRealizados.some(s => s.value === numericValue)) {
      setServiciosRealizados([
        ...serviciosRealizados,
        { value: numericValue, label: servicio.label }
      ]);
    }
  };

  useEffect(() => {
    if (success) {
    //  message.success('Consulta de optometría neonatal creada exitosamente');
      setServiciosRealizados([]);
      setServiciosProximos([]);
      setSelectedPaciente(null);
      setSelectedSucursal(null);
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
    }
  }, [success]);


  const handleRemoveServicioRealizado = (value: number) => {
    setServiciosRealizados(serviciosRealizados.filter(s => s.value !== value));
  };

  const handleAddServicioProximo = (value: string, servicio: any): void => {
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && !serviciosProximos.some(s => s.value === numericValue)) {
      setServiciosProximos([
        ...serviciosProximos,
        { value: numericValue, label: servicio.label }
      ]);
    }
  };


  const handleRemoveServicioProximo = (value: number) => {
    setServiciosProximos(serviciosProximos.filter(s => s.value !== value));
  };

  const handleSubmit = async (values: FormValues, formikHelpers: any): Promise<void> => {
    if (serviciosRealizados.length === 0) {
      message.error('Debe seleccionar al menos un servicio realizado');
      return;
    }

    const validServiciosRealizados = serviciosRealizados
      .filter(s => s.value && typeof s.value === 'number')
      .map(s => s.value);

    const validServiciosProximos = serviciosProximos
      .filter(s => s.value && typeof s.value === 'number')
      .map(s => s.value);

    if (validServiciosRealizados.length === 0) {
      message.error('No hay servicios realizados válidos seleccionados');
      return;
    }

    const formData = {
      ...values,
      servicios_realizados: validServiciosRealizados,
      servicios_proximos: validServiciosProximos,
    };

    try {
      await dispatch(createOptometriaNeonatos(formData));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      message.error('Error al guardar la consulta: ' + errorMessage);
      console.error('Error al guardar la consulta:', error);
    }
  };

  return (
    <div>
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <Spin size="large" tip="Guardando..." />
        </div>
      )}
      <Formik
        innerRef={formikRef}
        initialValues={{
          pacienteId: '',
          sucursalId: '',
          edad: '',
          fecha_atencion: '',
          motivo_consulta: '',
          antecedente_ocular: '',
          antecedente_personal: '',
          antecedente_familiar: '',
          medicamentos: '',
          tratamiento_anterior: '',
          desarrollo: '',
          nacimiento: '',
          parto: '',
          gateo: '',
          lenguaje: '',
          complicacion_prenatal: '',
          complicacion_perinatal: '',
          complicacion_postnatal: '',
          tambor_optocinetico_AO: '',
          fija: '',
          sigue: '',
          mantiene: '',
          test_referencial_OD: '',
          test_referencial_OI: '',
          test_referencial_AO: '',
          ru_esfera_OD: '',
          ru_cilindro_OD: '',
          ru_eje_OD: '',
          ru_p_base_OD: '',
          ru_add_OD: '',
          ru_esfera_OI: '',
          ru_cilindro_OI: '',
          ru_eje_OI: '',
          ru_p_base_OI: '',
          ru_add_OI: '',
          ru_tipo_lentes: '',
          filtros: '',
          tiempo: '',
          tipo_aro: '',
          segmento_anterior: '',
          polo_posterior: '',
          hirschberg: '',
          krismsky: '',
          versiones: '',
          ct_vp: '',
          reflejo_c: '',
          ducciones_OD: '',
          ducciones_OI: '',
          posicion_compensatoria: '',
          fotomotor_OD: '',
          consensual_OD: '',
          fotomotor_OI: '',
          consensual_OI: '',
          reflejo_OD: '',
          reflejo_OI: '',
          reflejo_AO: '',
          rf_esfera_OD: '',
          rf_cilindro_OD: '',
          rf_eje_OD: '',
          rf_p_base_OD: '',
          rf_add_OD: '',
          rf_esfera_OI: '',
          rf_cilindro_OI: '',
          rf_eje_OI: '',
          rf_p_base_OI: '',
          rf_add_OI: '',
          rf_tipo_lentes: '',
          PD: '',
          uso: '',
          conducta_seguir: '',
          fecha_proxima_consulta: '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ setFieldValue, isSubmitting, values, resetForm }) => (
          <Form className="space-y-6 p-6 bg-white shadow-lg rounded-lg">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block mb-2 font-medium">Pacientes</label>
                <PacientesSelect
                  onChange={(value, paciente) => {
                    handlePacienteChange(value, paciente);
                    setFieldValue('pacienteId', value);
                    setFieldValue('edad', paciente?.edad || '');
                  }}
                  className="w-full"
                />
                <ErrorMessage name="pacienteId" component="div" className="text-red-500" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block mb-2 font-medium">Sucursal</label>
                <SucursalesSelect
                  onChange={(value, sucursal) => {
                    setSelectedSucursal(sucursal);
                    setFieldValue('sucursalId', value);
                  }}
                  className="w-full"
                />
                <ErrorMessage name="sucursalId" component="div" className="text-red-500" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Edad</label>
                <Field
                  name="edad"
                  className="w-full p-2 border rounded bg-gray-100"
                  readOnly
                />
                <ErrorMessage name="edad" component="div" className="text-red-500" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Fecha de atención</label>
                <Field
                  type="date"
                  name="fecha_atencion"
                  className="w-full p-2 border rounded"
                />
                <ErrorMessage name="fecha_atencion" component="div" className="text-red-500" />
              </div>
            </div>

            <div>
              <label className="block mb-2 font-medium">Motivo de consulta</label>
              <Field
                as="textarea"
                name="motivo_consulta"
                className="w-full p-2 border rounded h-40"
                maxLength={10000}
              />
              <ErrorMessage name="motivo_consulta" component="div" className="text-red-500" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block mb-2 font-medium">Antecedentes Oculares</label>
                <Field name="antecedente_ocular" className="w-full p-2 border rounded" />
                <ErrorMessage name="antecedente_ocular" component="div" className="text-red-500" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Antecedentes Personales</label>
                <Field name="antecedente_personal" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Antecedentes Familiares</label>
                <Field name="antecedente_familiar" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block mb-2 font-medium">Medicamentos</label>
                <Field name="medicamentos" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Tratamientos anteriores</label>
                <Field name="tratamiento_anterior" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Desarrollo del infante</label>
                <Field name="desarrollo" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block mb-2 font-medium">Nacimiento</label>
                <Field name="nacimiento" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Parto</label>
                <Field name="parto" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Gateo</label>
                <Field name="gateo" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Lenguaje</label>
                <Field name="lenguaje" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block mb-2 font-medium">Complicaciones Prenatales</label>
                <Field name="complicacion_prenatal" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Perinatales</label>
                <Field name="complicacion_perinatal" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Postnatales</label>
                <Field name="complicacion_postnatal" className="w-full p-2 border rounded" />
              </div>
            </div>

            <h4 className="text-lg font-bold">AGUDEZA VISUAL:</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block mb-2 font-medium">Tambor Optocinético AO</label>
                <Field name="tambor_optocinetico_AO" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Fija</label>
                <Field name="fija" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Sigue</label>
                <Field name="sigue" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Mantiene</label>
                <Field name="mantiene" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block mb-2 font-medium">Test mirada preferencial OD</label>
                <Field name="test_referencial_OD" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">OI</label>
                <Field name="test_referencial_OI" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">AO</label>
                <Field name="test_referencial_AO" className="w-full p-2 border rounded" />
              </div>
            </div>

            <h4 className="text-lg font-bold">RECETA EN USO</h4>
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                bordered
                className="mb-6"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block mb-2 font-medium">Tipo de lentes</label>
                <Field name="ru_tipo_lentes" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Filtros</label>
                <Field name="filtros" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Tiempo</label>
                <Field name="tiempo" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Tipo de Aro</label>
                <Field name="tipo_aro" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="text-md font-bold">Segmento Anterior</h5>
                <Field as="textarea" name="segmento_anterior" className="w-full p-2 border rounded mt-2" />
              </div>

              <div>
                <h5 className="text-md font-bold">Polo Posterior</h5>
                <Field as="textarea" name="polo_posterior" className="w-full p-2 border rounded mt-2" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block mb-2 font-medium">Hirschberg</label>
                <Field name="hirschberg" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Krismsky</label>
                <Field name="krismsky" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div>
              <label className="block mb-2 font-medium">VERSIONES:</label>
              <Field
                as="textarea"
                name="versiones"
                className="w-full p-2 border rounded h-40"
                maxLength={10000}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block mb-2 font-medium">CT: VP:</label>
                <Field name="ct_vp" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Reflejo Cocleopalpebral</label>
                <Field name="reflejo_c" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Ducciones:OD</label>
                <Field name="ducciones_OD" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">OI</label>
                <Field name="ducciones_OI" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
              <div>
                <label className="block mb-2 font-medium">Posición Compensatoria</label>
                <Field name="posicion_compensatoria" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block mb-2 font-medium">Reflejos Pupilares: Fotomotor/OD</label>
                <Field name="fotomotor_OD" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Consensual</label>
                <Field name="consensual_OD" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Fotomotor:OI</label>
                <Field name="fotomotor_OI" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">Consensual</label>
                <Field name="consensual_OI" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block mb-2 font-medium">Reflejo retinoscopico OD:</label>
                <Field name="reflejo_OD" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">OI:</label>
                <Field name="reflejo_OI" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">AO:</label>
                <Field name="reflejo_AO" className="w-full p-2 border rounded" />
              </div>
            </div>

            <h4 className="text-lg font-bold">RECETA FINAL</h4>
            <div className="overflow-x-auto">
              <Table
                columns={columns}
                dataSource={dataSourceFinal}
                pagination={false}
                bordered
                className="mb-6"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block mb-2 font-medium">Tipo Lentes</label>
                <Field name="rf_tipo_lentes" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">PD:</label>
                <Field name="PD" className="w-full p-2 border rounded" />
              </div>

              <div>
                <label className="block mb-2 font-medium">USO:</label>
                <Field name="uso" className="w-full p-2 border rounded" />
              </div>
            </div>

            <div>
              <label className="block mb-2 font-medium">CONDUCTA A SEGUIR:</label>
              <Field
                as="textarea"
                name="conducta_seguir"
                className="w-full p-2 border rounded h-40"
                maxLength={10000}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
              <div>
                <label className="block mb-2 font-medium">Fecha de próxima cita</label>
                <Field
                  type="date"
                  name="fecha_proxima_consulta"
                  className="w-full p-2 border rounded"
                  required
                />
                <ErrorMessage name="fecha_proxima_consulta" component="div" className="text-red-500" />
              </div>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div>
                  <label className="block mb-2 font-medium">Servicios Realizados</label>
                  <ServiciosClinicosSelect
                    placeholder="Seleccione servicios realizados"
                    className="w-full"
                    onChange={handleAddServicioRealizado}
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {serviciosRealizados.map((servicio) => (
                      <div
                        key={servicio.value}
                        className="flex items-center bg-white border border-gray-300 rounded-full px-3 py-1"
                      >
                        <span>{servicio.label}</span>
                        <CloseCircleOutlined
                          className="ml-2 cursor-pointer text-red-500"
                          onClick={() => handleRemoveServicioRealizado(servicio.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <div>
                  <label className="block mb-2 font-medium">Próximos Servicios</label>
                  <ServiciosClinicosSelect
                    placeholder="Seleccione próximos servicios"
                    className="w-full"
                    onChange={handleAddServicioProximo}
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    {serviciosProximos.map((servicio) => (
                      <div
                        key={servicio.value}
                        className="flex items-center bg-white border border-gray-300 rounded-full px-3 py-1"
                      >
                        <span>{servicio.label}</span>
                        <CloseCircleOutlined
                          className="ml-2 cursor-pointer text-red-500"
                          onClick={() => handleRemoveServicioProximo(servicio.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>

            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
              className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              Guardar Consulta
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default OptometriasNeonatas;